import React from 'react';

import { ExitToApp } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip } from '@mui/material';
import { action, computed, observable, makeObservable, when } from 'mobx';
import { observer } from 'mobx-react';
import ConsultaProcesso, {
    CONSULTA_PROCESSO_ABA_GE_REQUEST_KEY,
    CONSULTA_PROCESSO_ABA_GE_RESPONSE_KEY
} from '../components/ConsultaProcesso';
import BotaoRecarregarProcessos from '../components/escaninho/BotaoRecarregarProcessos';
import LogusService from '../services/LogusService';
import ProcessoService from '../services/ProcessoService';
import UsuarioStore from '../store/UsuarioStore';
import { AppStore } from '../store/AppStore';
import MensagemStore from '../store/MensagemStore';
import ProcessoStore from '../store/ProcessoStore';
import RouterStore from '../store/RouterStore';
import UtilNumeroProcesso from '../utils/UtilNumeroProcesso';
import BemTeVi from './BemTeVi';
import GraficosGerenciamentoGE from './GraficosGerenciamentoGE';
import Helper from './Helper';
import Jurisprudencia from './Jurisprudencia';
import Notificacao from './Notificacao';
import RelatorioAndTramTrans from './RelatorioAndTramTrans';
import SettingsPopover from './SettingsPopover';
import GabineteStore from '../store/GabineteStore';
import AlertaFalhaNoEnvelope from './AlertaFalhaNoEnvelope';
import MenuControleGabinete from '@/components/controle-gabinete/MenuControleGabinete';


class TopBarAction extends React.Component<any> {
    modalConfirmacaoSair = false;
    tituloOriginalDocumento = '';
    intervaloNotificacaoAba: number | null = null;
    indicadorNotificacaoVisivel = false;

    componentDidMount() {
        window.addEventListener('storage', this.responderConsultaProcessoAbaGe);
        window.addEventListener('beforeunload', this.avisarFechamentoComEspelhosAbertos);
        window.addEventListener('pagehide', this.fecharEspelhosFilhos);
        window.addEventListener('focus', this.limparIndicadorNotificacaoAba);
        document.addEventListener('visibilitychange', this.limparIndicadorNotificacaoAbaQuandoVisivel);
    }

    componentWillUnmount() {
        window.removeEventListener('storage', this.responderConsultaProcessoAbaGe);
        window.removeEventListener('beforeunload', this.avisarFechamentoComEspelhosAbertos);
        window.removeEventListener('pagehide', this.fecharEspelhosFilhos);
        window.removeEventListener('focus', this.limparIndicadorNotificacaoAba);
        document.removeEventListener('visibilitychange', this.limparIndicadorNotificacaoAbaQuandoVisivel);
        this.limparIndicadorNotificacaoAba();
    }

    avisarFechamentoComEspelhosAbertos = (event: BeforeUnloadEvent) => {
        if (!RouterStore.possuiJanelasEspelhoAbertas) {
            return;
        }

        event.preventDefault();
        event.returnValue = 'Existem abas do espelho do processo que dependem desta aba do Gabinete Eletrônico. Se você sair, essas abas também serão fechadas.';
        return event.returnValue;
    }

    fecharEspelhosFilhos = () => {
        if (RouterStore.possuiJanelasEspelhoAbertas) {
            RouterStore.fecharJanelasEspelhoAbertas();
        }
    }

    responderConsultaProcessoAbaGe = async (event: StorageEvent) => {
        if (event.key !== CONSULTA_PROCESSO_ABA_GE_REQUEST_KEY || !event.newValue) {
            return;
        }

        const request = JSON.parse(event.newValue);
        if (!request?.id || !request?.numeroProcesso) {
            return;
        }

        await this.aguardarCargaEstavelDoEscaninho();

        const processo = this.encontrarProcessoNaStore(request.numeroProcesso);
        if (processo) {
            ProcessoService.mostraProcessoNoEscaninho(processo);
            MensagemStore.apresentarInformacao('Filtrado pelo espelho do processo.');
            this.notificarAbaSemFoco();
        }

        localStorage.setItem(CONSULTA_PROCESSO_ABA_GE_RESPONSE_KEY, JSON.stringify({
            id: request.id,
            encontrado: !!processo
        }));
    }

    notificarAbaSemFoco = () => {
        if (!document.hidden && document.hasFocus()) {
            return;
        }

        this.iniciarNotificacaoAba();
    }

    limparIndicadorNotificacaoAbaQuandoVisivel = () => {
        if (!document.hidden) {
            this.limparIndicadorNotificacaoAba();
        }
    }

    limparIndicadorNotificacaoAba = () => {
        if (this.intervaloNotificacaoAba) {
            window.clearInterval(this.intervaloNotificacaoAba);
            this.intervaloNotificacaoAba = null;
        }

        if (this.tituloOriginalDocumento) {
            document.title = this.tituloOriginalDocumento;
        }

        this.tituloOriginalDocumento = '';
        this.indicadorNotificacaoVisivel = false;
    }

    iniciarNotificacaoAba = () => {
        if (!this.tituloOriginalDocumento) {
            this.tituloOriginalDocumento = document.title;
        }

        if (this.intervaloNotificacaoAba) {
            return;
        }

        this.iniciarIntervaloNotificacaoAba();
    }

    iniciarIntervaloNotificacaoAba = () => {
        if (this.intervaloNotificacaoAba) {
            return;
        }

        this.alternarIndicadorNotificacaoAba();
        this.intervaloNotificacaoAba = window.setInterval(() => {
            this.alternarIndicadorNotificacaoAba();
        }, 800);
    }

    alternarIndicadorNotificacaoAba = () => {
        this.indicadorNotificacaoVisivel = !this.indicadorNotificacaoVisivel;
        document.title = this.indicadorNotificacaoVisivel
            ? '(1) Filtrado pelo espelho'
            : this.tituloOriginalDocumento;
    }

    aguardarCargaEstavelDoEscaninho = async () => {
        await new Promise<void>((resolve) => {
            when(
                () => ProcessoStore.carregouProcessosNoEscaninho && !ProcessoStore.carregandoProcessosNoEscaninho,
                () => resolve()
            );
        });

        await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }

    encontrarProcessoNaStore = (numeroProcesso: {
        numero: string;
        digito: string;
        ano: string;
        orgao: string;
        regional: string;
        vara: string
    }) => {
        const numeroNormalizado = numeroProcesso.numero
            ? numeroProcesso.numero.padStart(7, '0')
            : '';

        const processosEncontrados = ProcessoStore._processos.filter(processo => {
            const numeroObj = UtilNumeroProcesso.matchObjetoNumeroProcesso(processo.numeroProcesso);
            const numeroSemZeros = numeroObj.numero.replace(/^0+/, '');
            const numeroDigitado = numeroProcesso.numero?.replace(/^0+/, '') || '';

            return (
                (!numeroDigitado || numeroSemZeros.startsWith(numeroDigitado)) &&
                (!numeroProcesso.digito || numeroObj.digito.startsWith(numeroProcesso.digito)) &&
                (!numeroProcesso.ano || numeroObj.ano.startsWith(numeroProcesso.ano)) &&
                (!numeroProcesso.orgao || numeroObj.justica.startsWith(numeroProcesso.orgao)) &&
                (!numeroProcesso.regional || numeroObj.tribunal.startsWith(numeroProcesso.regional)) &&
                (!numeroProcesso.vara || numeroObj.vara.startsWith(numeroProcesso.vara))
            );
        });

        if (processosEncontrados.length === 1) {
            return processosEncontrados[0];
        }

        const numeroCompletoInformado =
            numeroNormalizado.length === 7 &&
            numeroProcesso.digito?.length === 2 &&
            numeroProcesso.ano?.length === 4 &&
            numeroProcesso.orgao?.length === 1 &&
            numeroProcesso.regional?.length === 2 &&
            numeroProcesso.vara?.length === 4;

        if (!numeroCompletoInformado) {
            return undefined;
        }

        const numeroCompleto =
            numeroNormalizado + '-' +
            numeroProcesso.digito + '.' +
            numeroProcesso.ano + '.' +
            numeroProcesso.orgao + '.' +
            numeroProcesso.regional + '.' +
            numeroProcesso.vara;

        return ProcessoService.retornaProcessoNaStore(numeroCompleto);
    }

    cancelarModalSair = () => {
        this.modalConfirmacaoSair = false;
    };

    openModalSair = () => {
        this.modalConfirmacaoSair = true;
    };

    confirmarSairSistema = () => {
        this.modalConfirmacaoSair = false;
        AppStore.fecharApp();
    };

    constructor(props: any) {
        super(props);

        makeObservable(this, {
            modalConfirmacaoSair: observable,
            cancelarModalSair: action,
            openModalSair: action,
            confirmarSairSistema: action,
            nome: computed
        });
    }

    renderModalConfirmacaoSair() {
        return (
            <Dialog open={this.modalConfirmacaoSair}
                onClose={this.cancelarModalSair}
                style={{ zIndex: 1503 }}
            >
                <DialogTitle>
                    Confirmação
                </DialogTitle>
                <DialogContent>
                    Deseja realmente sair do sistema?
                </DialogContent>
                <DialogActions>
                    <Button variant='text' color='secondary' onClick={this.cancelarModalSair}>
                        Não
                    </Button>
                    <Button variant='contained' color='primary' onClick={this.confirmarSairSistema}>
                        Sim
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    get nome() {
        return UsuarioStore.usuario?.nome || '';
    }

    ehCPF(valor) {
        // Remove espaços extras nas pontas
        valor = valor.trim();

        // Expressão regular para CPF com ou sem pontuação
        const regexCPF = /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/;

        // Verifica se corresponde a um CPF
        if (regexCPF.test(valor)) {
            return true;
        } else {
            return false;
        }
    }

    render() {
        return (
            <div>
                {UsuarioStore.isUsuarioLogado ?
                    <span className='aCenter' style={{ display: 'flex', fontSize: '0.7em' }}>
                        <AlertaFalhaNoEnvelope />
                        <GraficosGerenciamentoGE />
                        <Jurisprudencia />
                        <RelatorioAndTramTrans />
                        <BemTeVi />
                        <Helper />
                        {RouterStore.isAlgumEscaninho && !RouterStore.isEscaninhoPauta && <SettingsPopover />}
                        <ConsultaProcesso />
                        <Notificacao />
                        {/* <TourIcon /> */}
                        <span style={{
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            paddingRight: '10px'
                        }}>
                            <span className='data-hj-suppress'>
                                {UsuarioStore.usuario?.login ? ' ' + UsuarioStore.usuario.login + ' ' : ''}
                                -
                                {' ' + (this.ehCPF(this.nome) ? GabineteStore.recuperarNomeServidor(this.nome) : this.nome) + ' '}
                                {UsuarioStore.localizacao ? '(' + UsuarioStore.localizacao.siglaOrgaoJulgador + ')' : ''}
                            </span>
                        </span>
                        <span style={{ color: 'white', display: 'flex' }}>
                            {RouterStore.isControleGabinete && (
                                <MenuControleGabinete />
                            )}
                        </span>
                        <BotaoRecarregarProcessos />
                        <Tooltip title='Sair do sistema'>
                            <div style={{ color: 'white' }}>
                                <IconButton color='inherit' onClick={this.openModalSair} aria-label='Sair do Gabinete Eletrônico'>
                                    <ExitToApp />
                                </IconButton>
                            </div>
                        </Tooltip>
                    </span>
                    :
                    <div style={{ color: 'white' }}>
                        <IconButton color='inherit' onClick={LogusService.logout} title="Sair do sistema" aria-label='Sair do Gabinete Eletrônico'>
                            <ExitToApp />
                        </IconButton>
                    </div>
                }
                {this.renderModalConfirmacaoSair()}
            </div>
        );
    }
}

export default observer(TopBarAction);
